import isEqual from 'lodash/isEqual'
import PropTypes from 'prop-types'
import React from 'react'

import Spinner from '../components/Spinner'
import cn from '../utils/tailwind/cn'

function useStyleFromButtonSize(size) {
  const getPaddingVertical = React.useCallback(() => {
    switch (size) {
      case 'small':
      case 'tiny':
      case 'xs':
        return 1
      case '2xs':
        return 0
      default:
        return 2
    }
  }, [size])
  const [paddingVerticalStyle, setPaddingVerticalStyle] =
    React.useState(getPaddingVertical)

  const getPaddingHorizontal = React.useCallback(() => {
    switch (size) {
      case 'small':
        return 3
      case 'tiny':
      case 'xs':
      case '2xs':
        return 2
      default:
        return 6
    }
  }, [size])
  const [paddingHorizontalStyle, setPaddingHorizontalStyle] =
    React.useState(getPaddingHorizontal)

  const getFontSize = React.useCallback(() => {
    switch (size) {
      case 'small':
        return 'sm'
      case 'tiny':
      case 'xs':
      case '2xs':
        return 'xs'
      default:
        return 'base'
    }
  }, [size])
  const [fontSize, setFontSize] = React.useState(getFontSize)

  React.useEffect(() => {
    setPaddingVerticalStyle(getPaddingVertical)
    setPaddingHorizontalStyle(getPaddingHorizontal)
    setFontSize(getFontSize)
  }, [getPaddingVertical, getPaddingHorizontal, getFontSize])

  return { paddingVerticalStyle, paddingHorizontalStyle, fontSize }
}

function ButtonClear({
  onClick,
  disabled,
  children,
  className,
  loading,
  hoverColor,
  size,
  dataCy,
}: {
  onClick: () => void,
  disabled?: boolean,
  children?: React.ReactNode,
  className?: string,
  loading?: boolean,
  hoverColor?: string,
  size?: string,
  dataCy?: string,
}) {
  const { paddingVerticalStyle, paddingHorizontalStyle, fontSize } =
    useStyleFromButtonSize(size)

  const classes = disabled
    ? `font-${fontSize} inline-flex items-center justify-center py-${paddingVerticalStyle} px-${paddingHorizontalStyle} leading-6 font-medium rounded-md text-gray-500 bg-white/50`
    : `font-${fontSize} inline-flex items-center justify-center py-${paddingVerticalStyle} px-${paddingHorizontalStyle} leading-6 font-medium rounded-md text-gray-500 hover:bg-${
        hoverColor || 'dark-10'
      } focus:outline-none focus:border-gray-100 focus:shadow-outline-blue active:bg-gray-200 transition duration-150 ease-in-out`

  return (
    <button
      className={cn(classes, className)}
      data-cy={dataCy}
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {loading && <Spinner className="mr-2" color="onLight" />}
      {children}
    </button>
  )
}

ButtonClear.defaultProps = {
  onClick: () => {},
  disabled: false,
  className: '',
}

ButtonClear.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
}

function areEqual(p, n) {
  return (
    p.disabled === n.disabled &&
    p.className === n.className &&
    isEqual(p.children, n.children) &&
    isEqual(p.onClick, n.onClick) &&
    p.loading === n.loading &&
    p.paddingHorizontal === n.paddingHorizontal &&
    p.hoverColor === n.hoverColor &&
    p.size !== n.size
  )
}

export default React.memo(ButtonClear, areEqual)
